import { FC, ReactNode, useMemo } from 'react'
import { NetworthBreakdownDate } from 'components/features/future/types'
import styles from 'components/features/pfm/StackedChart/BreakdownDate.styl'
import orderBy from 'lodash/orderBy'
import { cssVar } from 'helpers/styleHelpers'
import { splitAndSortBreakdownDate, getCategoriesMap } from 'components/features/future/helpers'
import { FutureColorPicker } from 'components/features/future/FutureColorPicker/FutureColorPicker'
import { isAssetsCategory } from 'helpers/categoriesHelpers'


type BreakdownDateProps = {
  item: NetworthBreakdownDate
  ratio: number
}
export const BreakdownDate: FC<BreakdownDateProps> = (props) => {
  const { item, ratio } = props

  const categoriesMap = useMemo(() => {
    const { assets, debts } = splitAndSortBreakdownDate(item)
    return {
      ...getCategoriesMap(assets, 'assets'),
      ...getCategoriesMap(debts, 'debts'),
    }
  }, [item])

  const { positive, negative } = useMemo(() => {
    if (!item) {
      return { positive: null, negative: null }
    }

    let categoriesOrder = item.values.map(({ category, amount }, index) => {
      const isAsset = isAssetsCategory(category)
      const sign = isAsset ? 1 : -1
      const signedAmount = sign * amount
      const isAboveZero = signedAmount > 0
      const absAmount = Math.abs(amount)


      return {
        index,
        typeRate:
           isAsset && isAboveZero ? 0 :
             !isAsset && isAboveZero ? 1 :
               isAsset && !isAboveZero ? 2 :
                 3,
        positiveValue: isAboveZero ? absAmount : 0,
        negativeValue: !isAboveZero ? absAmount : 0,
        signedAmount,
        absAmount,
        category,
      }
    })
    categoriesOrder = orderBy(categoriesOrder, ['typeRate', 'positiveValue', 'negativeValue'], ['asc', 'asc', 'desc'])

    return categoriesOrder.reduce((acc, { index, absAmount, signedAmount }) => {
      const { category } = item.values[index]
      const { type, index: categoryIndex } = categoriesMap[category]

      const isAsset = type === 'assets'
      const colorIndex = isAsset ? categoryIndex : categoryIndex

      const blockChild = (
        <FutureColorPicker
          key={ index }
          className={ styles.block }
          data-category={ category }
          style={ cssVar({
            '--amount': `${ absAmount * ratio }px`,
          }) }

          type={ type }
          index={ colorIndex }
        />
      )

      if (signedAmount > 0) {
        acc.positive.push(blockChild)
      } else {
        acc.negative.push(blockChild)
      }

      return acc
    }, {
      positive: [] as ReactNode[],
      negative: [] as ReactNode[],
    })

  }, [item, ratio, categoriesMap])

  if (!positive || !negative) {
    return null
  }

  return (
    <div className={ styles.BreakdownDate }>
      <div className={ styles.positive }>
        { positive }
      </div>
      <div className={ styles.negative }>
        { negative }
      </div>
    </div>
  )
}
